import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Sparkles, Star } from 'lucide-react';
import {
  googleReviewsApi,
  STAR_LABELS,
  type GoogleReview,
} from '@/lib/api/google-reviews.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { formatDate } from '@/lib/format.js';

type ReviewFilter = 'all' | 'needs' | 'responded' | 'low';

function stars(n: number) {
  return '⭐'.repeat(Math.max(0, Math.min(5, n)));
}

export function ReviewsPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<ReviewFilter>('all');
  const [draftReviewId, setDraftReviewId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');

  const configQuery = useQuery({
    queryKey: ['google-reviews-config'],
    queryFn: googleReviewsApi.getConfig,
  });

  const statsQuery = useQuery({
    queryKey: ['google-reviews-stats'],
    queryFn: googleReviewsApi.getStats,
    enabled: configQuery.data?.data.connected,
  });

  const reviewsQuery = useQuery({
    queryKey: ['google-reviews-list', filter],
    queryFn: () =>
      googleReviewsApi.listReviews({
        responded:
          filter === 'needs' ? false : filter === 'responded' ? true : undefined,
      }),
    enabled: configQuery.data?.data.connected,
  });

  const aiDraftMut = useMutation({
    mutationFn: (review: GoogleReview) =>
      googleReviewsApi.aiDraft(review.reviewId, {
        reviewText: review.comment,
        rating: STAR_LABELS[review.starRating] ?? 5,
      }),
    onSuccess: (res) => {
      setDraftText(res.data.draft);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const replyMut = useMutation({
    mutationFn: ({ reviewId, comment }: { reviewId: string; comment: string }) =>
      googleReviewsApi.postReply(reviewId, comment),
    onSuccess: () => {
      toastSuccess('Reply posted successfully');
      setDraftReviewId(null);
      setDraftText('');
      queryClient.invalidateQueries({ queryKey: ['google-reviews-list'] });
      queryClient.invalidateQueries({ queryKey: ['google-reviews-stats'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const reviews = useMemo(() => {
    const rows = reviewsQuery.data?.data ?? [];
    if (filter !== 'low') return rows;
    return rows.filter((r) => (STAR_LABELS[r.starRating] ?? 5) <= 3);
  }, [reviewsQuery.data?.data, filter]);

  const stats = statsQuery.data?.data;
  const connected = configQuery.data?.data.connected;
  const needsLocation = configQuery.data?.data.needsLocation;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-950">Google Reviews</h1>
          <p className="text-sm text-slate-600">
            {connected
              ? '🟢 Connected'
              : needsLocation
                ? '🟡 Connected — select a location in Settings'
                : '🔴 Not connected'}
          </p>
        </div>
        <div className="flex gap-2">
          {connected ? (
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['google-reviews-stats'] });
                queryClient.invalidateQueries({ queryKey: ['google-reviews-list'] });
              }}
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          ) : (
            <a
              href="/settings/store"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
            >
              {needsLocation ? 'Select Location' : 'Connect Google'}
            </a>
          )}
        </div>
      </header>

      {!connected ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <Star className="mx-auto h-10 w-10 text-amber-400" />
          <p className="mt-4 text-slate-600">
            {needsLocation
              ? 'Google is connected, but this store still needs a Business Profile location. Open Settings → Store to pick one.'
              : 'Connect your Google Business Profile to view reviews and respond with AI-assisted drafts.'}
          </p>
        </div>
      ) : (
        <>
          {stats ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Avg Rating" value={`⭐ ${stats.averageRating}`} />
                <StatCard label="Total Reviews" value={String(stats.totalReviews)} />
                <StatCard label="This Month" value={String(stats.newReviewsThisMonth)} />
                <StatCard label="Response Rate" value={`${stats.responseRate}%`} />
              </div>

              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="font-bold text-slate-950">Rating breakdown</h2>
                <div className="mt-4 space-y-2">
                  {([5, 4, 3, 2, 1] as const).map((star) => {
                    const count = stats.ratingBreakdown[star] ?? 0;
                    const pct = stats.totalReviews
                      ? Math.round((count / stats.totalReviews) * 100)
                      : 0;
                    return (
                      <div key={star} className="flex items-center gap-3 text-sm">
                        <span className="w-8">{star}⭐</span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-16 text-right text-slate-600">
                          {count} ({pct}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          ) : null}

          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap gap-2 border-b border-slate-200 p-4">
              {(
                [
                  ['all', 'All'],
                  ['needs', 'Needs Response'],
                  ['responded', 'Responded'],
                  ['low', '⭐⭐⭐ and below'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                    filter === key ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="divide-y divide-slate-100">
              {reviews.map((review) => {
                const rating = STAR_LABELS[review.starRating] ?? 0;
                const isDrafting = draftReviewId === review.reviewId;
                return (
                  <article key={review.reviewId} className="p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-950">
                          {review.reviewer.displayName}
                        </p>
                        <p className="text-sm text-slate-500">{formatDate(review.createTime)}</p>
                      </div>
                      <span className="text-sm">{stars(rating)}</span>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap text-sm text-slate-800">
                      {review.comment || '(No comment)'}
                    </p>

                    {review.reviewReply?.comment ? (
                      <div className="mt-4 rounded-xl bg-green-50 p-4 text-sm text-slate-700">
                        <p className="font-semibold text-green-800">Your reply</p>
                        <p className="mt-1 whitespace-pre-wrap">{review.reviewReply.comment}</p>
                      </div>
                    ) : isDrafting ? (
                      <div className="mt-4 space-y-3 rounded-xl border border-slate-200 p-4">
                        <textarea
                          className="min-h-28 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                          value={draftText}
                          onChange={(e) => setDraftText(e.target.value)}
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold"
                            disabled={aiDraftMut.isPending}
                            onClick={() => aiDraftMut.mutate(review)}
                          >
                            Regenerate
                          </button>
                          <button
                            type="button"
                            className="rounded-xl bg-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                            disabled={!draftText.trim() || replyMut.isPending}
                            onClick={() =>
                              replyMut.mutate({ reviewId: review.reviewId, comment: draftText })
                            }
                          >
                            Post Reply →
                          </button>
                          <button
                            type="button"
                            className="rounded-xl px-3 py-1.5 text-sm text-slate-600"
                            onClick={() => {
                              setDraftReviewId(null);
                              setDraftText('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 flex gap-2">
                        <button
                          type="button"
                          className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold"
                          onClick={() => {
                            setDraftReviewId(review.reviewId);
                            setDraftText('');
                          }}
                        >
                          Write Reply
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
                          disabled={aiDraftMut.isPending}
                          onClick={() => {
                            setDraftReviewId(review.reviewId);
                            aiDraftMut.mutate(review);
                          }}
                        >
                          <Sparkles className="h-4 w-4" />
                          AI Draft
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
              {reviews.length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-500">No reviews found.</p>
              ) : null}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-2xl font-bold text-slate-950">{value}</p>
      <p className="mt-1 text-sm text-slate-600">{label}</p>
    </div>
  );
}
